/*
  Webastolaskuri – installed heater price by vehicle.
  Logic is a faithful port of the original priceclient.js component:
  items[make][model][productId] = "2050€" | 2050 | null ("-" = not available), products[productId] = { name, url },
  grouped into heater lines by name prefix. Only the presentation is new.
*/
(() => {
  const $ = (s, r = document) => r.querySelector(s);
  const esc = (s) => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const priceNum = (p) => Number(String(p).replace(/[^\d,.]/g, '').replace(',', '.')) || 0;
  const fmt = (n) => n.toLocaleString('fi-FI') + '€'; // store format: 2 050€, no decimals for whole euros

  class Laskuri {
    constructor(root) {
      this.root = root;
      this.t = JSON.parse($('[data-laskuri-strings]', root).textContent);
      this.state = { step: 1, make: '', model: '', choice: null };
      this.data = null;
      this.els = {
        steps: root.querySelectorAll('[data-step-indicator]'),
        gauge: $('[data-laskuri-gauge]', root),
        stage: $('[data-laskuri-stage]', root),
        live: $('[data-laskuri-live]', root),
        back: $('[data-laskuri-back]', root),
        restart: $('[data-laskuri-restart]', root),
      };
      this.els.back.addEventListener('click', () => this.go(this.state.step - 1));
      this.els.restart.addEventListener('click', () => { this.state = { step: 1, make: '', model: '', choice: null }; this.go(1); });
      this.els.steps.forEach((b) => b.addEventListener('click', () => {
        const n = Number(b.dataset.stepIndicator);
        if (n < this.state.step) this.go(n);
      }));
      this.lazyLoad();
    }

    lazyLoad() {
      const start = () => { if (!this.loading) this.load(); };
      if (this.root.dataset.eager === 'true' || !('IntersectionObserver' in window)) return start();
      const io = new IntersectionObserver(([en]) => { if (en.isIntersecting) { io.disconnect(); start(); } }, { rootMargin: '1200px' });
      io.observe(this.root);
      this.root.addEventListener('pointerenter', start, { once: true });
      this.root.addEventListener('focusin', start, { once: true });
    }

    async load() {
      this.loading = true;
      this.renderLoading();
      try {
        const config = JSON.parse($('[data-laskuri-config]', this.root).textContent);
        this.data = this.root.dataset.metaUrl ? await this.fetchMetaobjects(config) : await this.fetchSnapshot(config);
        this.heaters = config.heaters;
        this.makes = Object.keys(this.data.items).sort((a, b) => a.localeCompare(b, 'fi'));
        if (!this.makes.length) throw new Error('empty');
        this.restoreFromUrl();
        this.go(this.state.step, { silent: true });
      } catch (e) {
        this.renderError();
      }
    }

    /* Bundled snapshot: { products, items: { make: { model: { productId: "2050€" | null } } } } */
    async fetchSnapshot(config) {
      const res = await fetch(this.root.dataset.src, { credentials: 'omit' });
      if (!res.ok) throw new Error(res.status);
      const snap = await res.json();
      return { products: config.products, items: snap.items };
    }

    /* Metaobjects, 250 per request through the Section Rendering API, following paginate.next.url. */
    async fetchMetaobjects(config) {
      const load = async (url) => {
        const html = await (await fetch(url)).text();
        const node = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-laskuri-page]');
        if (!node) throw new Error('no data');
        return JSON.parse(node.textContent);
      };
      const withSection = (url) => { const u = new URL(url, location.origin); u.searchParams.set('section_id', 'laskuri-data'); return u.pathname + u.search; };
      const pages = [await load(this.root.dataset.metaUrl)];
      while (pages.at(-1).next && pages.length < 20) pages.push(await load(withSection(pages.at(-1).next)));
      const rows = pages.flatMap((p) => p.rows).sort((a, b) => a[0].localeCompare(b[0], 'fi') || a[2] - b[2]);
      const idByKey = Object.fromEntries(Object.entries(config.products).map(([id, p]) => [p.key, id]));
      const items = {};
      rows.forEach(([make, model, , prices]) => {
        const row = {};
        Object.entries(prices).forEach(([key, value]) => { if (idByKey[key]) row[idByKey[key]] = value; });
        (items[make] ||= {})[model] = row;
      });
      return { products: config.products, items };
    }

    restoreFromUrl() {
      const q = new URLSearchParams(location.search);
      const make = q.get('merkki');
      const model = q.get('malli');
      if (make && this.data.items[make]) {
        this.state.make = make;
        this.state.step = 2;
        if (model && this.data.items[make][model]) { this.state.model = model; this.state.step = 3; }
      }
    }

    syncUrl() {
      if (!this.root.hasAttribute('data-sync-url')) return;
      const q = new URLSearchParams(location.search);
      ['merkki', 'malli'].forEach((k) => q.delete(k));
      if (this.state.make) q.set('merkki', this.state.make);
      if (this.state.model) q.set('malli', this.state.model);
      const qs = q.toString();
      history.replaceState(history.state, '', location.pathname + (qs ? '?' + qs : '') + location.hash);
    }

    go(step, { silent } = {}) {
      step = Math.max(1, Math.min(4, step));
      if (step < 4) this.state.choice = step < 3 ? null : this.state.choice;
      if (step < 3) this.state.model = step < 2 ? '' : this.state.model;
      if (step < 2) this.state.make = '';
      this.state.step = step;
      const view = [null, this.viewMake, this.viewModel, this.viewSolution, this.viewSummary][step].call(this);
      this.swap(view, silent);
      this.els.steps.forEach((b) => {
        const n = Number(b.dataset.stepIndicator);
        b.setAttribute('aria-current', n === step ? 'step' : 'false');
        b.classList.toggle('is-done', n < step);
        b.disabled = n >= step;
        const val = b.querySelector('[data-step-value]');
        if (val) val.textContent = n === 1 ? this.state.make : n === 2 ? this.shortModel(this.state.model) : n === 3 && this.state.choice ? this.state.choice.heater : '';
      });
      this.els.gauge?.style.setProperty('--fill', ((step - 1) / 3).toFixed(3));
      this.els.back.hidden = step === 1;
      this.els.restart.hidden = step === 1;
      this.syncUrl();
    }

    swap(html, silent) {
      const stage = this.els.stage;
      const next = document.createElement('div');
      next.className = 'laskuri__view';
      next.innerHTML = html;
      stage.replaceChildren(next);
      requestAnimationFrame(() => next.classList.add('is-in'));
      this.bind(next);
      const heading = next.querySelector('[data-view-heading]');
      if (heading) this.els.live.textContent = heading.textContent;
      if (!silent && heading) {
        heading.focus({ preventScroll: true });
        const top = this.root.getBoundingClientRect().top;
        if (top < 0 || top > innerHeight * 0.6) this.root.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
      }
    }

    bind(view) {
      view.querySelectorAll('[data-make]').forEach((b) => b.addEventListener('click', () => { this.state.make = b.dataset.make; this.go(2); }));
      view.querySelectorAll('[data-model]').forEach((b) => b.addEventListener('click', () => { this.state.model = b.dataset.model; this.go(3); }));
      view.querySelectorAll('[data-choice]').forEach((b) => b.addEventListener('click', () => {
        this.state.choice = JSON.parse(b.dataset.choice);
        this.go(4);
      }));
      const filter = view.querySelector('[data-filter]');
      if (filter) {
        filter.addEventListener('input', () => {
          const q = filter.value.trim().toLowerCase();
          let shown = 0;
          view.querySelectorAll('[data-filter-item]').forEach((el) => {
            const hit = el.dataset.filterItem.toLowerCase().includes(q);
            el.hidden = !hit;
            if (hit) shown++;
          });
          view.querySelector('[data-filter-empty]').hidden = shown > 0;
        });
      }
    }

    /* ---------- views ---------- */
    head(n, title, sub) {
      return `<div class="laskuri__view-head"><span class="mono muted">${this.t.step} ${n}/4</span>
        <h3 class="h2" tabindex="-1" data-view-heading>${esc(title)}</h3>${sub ? `<p class="muted">${sub}</p>` : ''}</div>`;
    }

    viewMake() {
      const tiles = this.makes.map((m, i) => `<li data-filter-item="${esc(m)}" style="--i:${i}"><button type="button" class="laskuri__make" data-make="${esc(m)}">
        <span>${esc(m)}</span><span class="mono muted">${Object.keys(this.data.items[m]).length}</span></button></li>`).join('');
      return `${this.head(1, this.t.chooseMake, esc(this.t.chooseMakeHint))}
        <div class="laskuri__filter"><label class="visually-hidden" for="${this.root.id}-mf">${esc(this.t.searchMake)}</label>
        <input class="input" id="${this.root.id}-mf" type="search" placeholder="${esc(this.t.searchMake)}" data-filter autocomplete="off"></div>
        <ul class="laskuri__makes" role="list">${tiles}</ul>
        <p class="muted" data-filter-empty hidden>${esc(this.t.noMake)}</p>`;
    }

    // Strip only the trailing heater spec "(5kW lämmitin)"; keep chassis codes like "(F20/F21)".
    shortModel(model) { return (model || '').replace(/\s*\([^()]*\)\s*$/, ''); }

    viewModel() {
      const models = Object.keys(this.data.items[this.state.make]);
      const rows = models.map((m, i) => {
        const tag = (m.match(/\(([^)]*)\)\s*$/) || [])[1] || '';
        // Model names come from metaobjects (admin input): escape first, then mark up the year range.
        const name = esc(this.shortModel(m)).replace(/(\d{4}\s*-(?:\s*\d{2,4})?)/, '<span class="num">$1</span>');
        const prices = Object.values(this.data.items[this.state.make][m]).map(priceNum).filter((n) => n > 0);
        const from = prices.length ? Math.min(...prices) : null;
        return `<li data-filter-item="${esc(m)}" style="--i:${i}"><button type="button" class="laskuri__model" data-model="${esc(m)}">
          <span class="laskuri__model-name">${name}</span>
          ${tag ? `<span class="laskuri__tag mono">${esc(tag)}</span>` : '<span></span>'}
          <span class="laskuri__from">${from ? `<small>${esc(this.t.from)}</small> <span class="num">${fmt(from)}</span>` : ''}</span>
          <span class="laskuri__chev" aria-hidden="true">→</span></button></li>`;
      }).join('');
      return `${this.head(2, this.t.chooseModel.replace('{make}', this.state.make), esc(this.t.chooseModelHint))}
        <div class="laskuri__filter"><label class="visually-hidden" for="${this.root.id}-mo">${esc(this.t.searchModel)}</label>
        <input class="input" id="${this.root.id}-mo" type="search" placeholder="${esc(this.t.searchModel)}" data-filter autocomplete="off"></div>
        <ul class="laskuri__models" role="list">${rows}</ul>
        <p class="muted" data-filter-empty hidden>${esc(this.t.noModel)}</p>`;
    }

    groups() {
      const row = this.data.items[this.state.make][this.state.model];
      return this.heaters.map((h) => {
        const options = Object.keys(row)
          .filter((id) => this.data.products[id] && priceNum(row[id]) > 0 && this.data.products[id].name.startsWith(h.prefix))
          .map((id) => ({
            id,
            control: this.data.products[id].name.replace(h.prefix + ' ', ''),
            url: this.data.products[id].url,
            price: row[id],
            value: priceNum(row[id]),
          }))
          .sort((a, b) => a.value - b.value);
        return { ...h, options };
      }).filter((g) => g.options.length);
    }

    viewSolution() {
      const groups = this.groups();
      if (!groups.length) {
        return `${this.head(3, this.t.chooseSolution)}<div class="laskuri__empty"><p>${esc(this.t.noPrices)}</p>
          <a class="btn" href="${esc(this.root.dataset.contactUrl)}">${esc(this.t.askUs)}</a></div>`;
      }
      const cards = groups.map((g, gi) => {
        const opts = g.options.map((o) => {
          const choice = JSON.stringify({ heater: g.name, control: o.control, price: o.value, heaterUrl: g.url, controlUrl: o.url });
          return `<li><button type="button" class="laskuri__opt" data-choice='${esc(choice)}'>
            <span class="laskuri__opt-name">${esc(o.control)}</span>
            <span class="num laskuri__opt-price">${fmt(o.value)}</span>
            <span class="laskuri__opt-go" aria-hidden="true">${esc(this.t.select)}</span></button></li>`;
        }).join('');
        return `<article class="laskuri__heater" style="--i:${gi}">
          <header><span class="mono muted">${esc(this.t.heater)}</span>
            <h4 class="h3">${g.url ? `<a href="${esc(g.url)}">${esc(g.name)}</a>` : esc(g.name)}</h4></header>
          <div class="laskuri__opts-head mono muted"><span>${esc(this.t.control)}</span><span>${esc(this.t.installedPrice)}</span></div>
          <ul role="list">${opts}</ul></article>`;
      }).join('');
      return `${this.head(3, this.t.chooseSolution, `${esc(this.state.make)} ${esc(this.shortModel(this.state.model))}`)}
        <div class="laskuri__heaters">${cards}</div>
        <p class="laskuri__note muted">${esc(this.t.priceNote)}</p>`;
    }

    viewSummary() {
      const c = this.state.choice;
      const vehicle = `${this.state.make} ${this.state.model}`;
      const solution = `${c.heater} + ${c.control}`;
      const booking = new URL(this.root.dataset.bookingUrl, location.origin);
      booking.searchParams.set('ajoneuvo', vehicle);
      booking.searchParams.set('ratkaisu', `${solution} (${fmt(c.price)})`);
      return `${this.head(4, this.t.summary)}
        <div class="laskuri__summary">
          <dl class="laskuri__spec">
            <div><dt class="mono">${esc(this.t.vehicle)}</dt><dd>${esc(vehicle)}</dd></div>
            <div><dt class="mono">${esc(this.t.heater)}</dt><dd>${c.heaterUrl ? `<a href="${esc(c.heaterUrl)}">${esc(c.heater)}</a>` : esc(c.heater)}</dd></div>
            <div><dt class="mono">${esc(this.t.control)}</dt><dd>${c.controlUrl ? `<a href="${esc(c.controlUrl)}">${esc(c.control)}</a>` : esc(c.control)}</dd></div>
          </dl>
          <div class="laskuri__total">
            <span class="mono muted">${esc(this.t.installedPrice)}</span>
            <span class="laskuri__total-price num">${fmt(c.price)}</span>
            <p class="muted">${esc(this.t.priceNote)}</p>
            <div class="flow-actions">
              <a class="btn" href="${esc(booking.pathname + booking.search)}" data-laskuri-book>${esc(this.t.book)}<svg class="icon icon-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true"><path d="M4 12h15M13 6l6 6-6 6"/></svg></a>
              <a class="btn btn--ghost" href="tel:${esc(this.root.dataset.phone.replace(/\s/g, ''))}">${esc(this.root.dataset.phone)}</a>
            </div>
          </div>
        </div>`;
    }

    renderLoading() {
      this.els.stage.innerHTML = `<div class="laskuri__view is-in" aria-busy="true"><div class="laskuri__skeleton">${'<span></span>'.repeat(12)}</div>
        <p class="visually-hidden">${esc(this.t.loading)}</p></div>`;
    }

    renderError() {
      this.els.stage.innerHTML = `<div class="laskuri__view is-in laskuri__empty" role="alert"><p class="h3">${esc(this.t.error)}</p>
        <div class="flow-actions"><button type="button" class="btn btn--ghost" data-retry>${esc(this.t.retry)}</button>
        <a class="btn" href="tel:${esc(this.root.dataset.phone.replace(/\s/g, ''))}">${esc(this.root.dataset.phone)}</a></div></div>`;
      this.els.stage.querySelector('[data-retry]').addEventListener('click', () => this.load());
    }
  }

  const init = (scope = document) => scope.querySelectorAll('[data-laskuri]').forEach((el) => {
    if (!el._laskuri) el._laskuri = new Laskuri(el);
  });
  init();
  document.addEventListener('shopify:section:load', (e) => init(e.target));
  document.addEventListener('click', (e) => {
    const book = e.target.closest('[data-laskuri-book]');
    if (book && window.dataLayer) window.dataLayer.push({ event: 'laskuri_ajanvaraus' });
  });
})();
